import React, { useState } from "react";
import { Container, Content, Card, Header, Title, Subtitle } from "./shared/OnboardingStyles";
import { useAuthService } from "../../Context/AuthContext";
import { useNavigate } from "react-router";
import { useTranslate } from "@tolgee/react";
import useLanguagePreFill from "../../hooks/useLanguagePreFill";
import useMultiStepForm from "../../hooks/useMultiStepForm";
import VoiceCloningSettings from "../VoiceCloning/VoiceCloningSettings";
import ProgressDots from "../GenericComponents/ProgressDots";
import CulturalBgInfoModal from "./CulturalBgInfoModal";
import UserProfileForm from "./UserProfileForm";
import OnboardingReadyScreen from "./OnboardingReadyScreen";
import { useLinksquaredService } from "../../Context/LinksquaredContext";
import { useSearchParams, createSearchParams } from "react-router-dom";
import { useNotify } from "../../Helpers/Notifications";
import { buildUserProfile } from "../../Helpers/buildUserProfile";
import LocalStorage from "../../Helpers/LocalStorage";


const CreateNewUser = ({ countryCode, setIsLoading, setFirebaseTokens }) => {
  const { t } = useTranslate();
  const { step, next, back } = useMultiStepForm(3);
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("");
  const [culturalBg, setCulturalBg] = useState("");
  const [gender, setGender] = useState(null);
  const [birthday, setBirthday] = useState(null);
  const [isCulturalBgModalOpen, setIsCulturalBgModalOpen] = useState(false);
  const { getInvitedBy } = useLinksquaredService();
  const [searchParams, setSearchParams] = useSearchParams();
  const { createUser, translationsLanguages, pushToken, getCustomToken } = useAuthService();
  const navigate = useNavigate();
  const { notifyError } = useNotify();

  const canContinue = name.trim().length >= 3 && language !== "";

  useLanguagePreFill(language, setLanguage, translationsLanguages);

  const fetchCustomAuthTokenForDesktop = () => {
    getCustomToken(
      (response) => {
        setSearchParams((prev) => {
          prev.set("access_token", response.data.authentication_token);
        });
        navigate({
          pathname: "/desktop_login_success",
          search: createSearchParams(searchParams).toString(),
        });
        setIsLoading(false);
      },
      (error) => {
        notifyError(`${t("general.something_wrong")}`);
        setIsLoading(false);
        navigate("/");
      }
    );
  };

  const handleContinue = () => {
    if (!canContinue) return;
    setIsLoading(true);

    const dataObject = buildUserProfile({
      name, language, culturalBg, gender, birthday,
      countryCode,
      invitedBy: getInvitedBy(),
      pushToken,
    });

    setFirebaseTokens(() => {
      createUser(
        dataObject,
        (response) => {
          if (searchParams.get("desktop_login") === "true") {
            fetchCustomAuthTokenForDesktop();
          } else {
            setIsLoading(false);
            next();
          }
        },
        (error) => {
          notifyError(`${t("general.something_wrong")}`);
          setIsLoading(false);
        }
      );
    });
  };

  return (
    <Container>
      {isCulturalBgModalOpen && (
        <CulturalBgInfoModal openModal={() => setIsCulturalBgModalOpen(false)} />
      )}

      <Content>
        <Card>
          {step !== 3 && (
            <ProgressDots
              step={step}
              total={2}
              onBack={step === 2 ? back : null}
            />
          )}

          {step === 1 && (
            <>
              <Header>
                <Title>{t("authentication.onboarding.header_title")}</Title>
                <Subtitle>{t("authentication.onboarding.header_subtitle")}</Subtitle>
              </Header>
              <UserProfileForm
                name={name} setName={setName}
                language={language} setLanguage={setLanguage}
                translationsLanguages={translationsLanguages}
                birthday={birthday} setBirthday={setBirthday}
                gender={gender} setGender={setGender}
                culturalBg={culturalBg} setCulturalBg={setCulturalBg}
                onInfoClick={() => setIsCulturalBgModalOpen(true)}
                canContinue={canContinue}
                onContinue={handleContinue}
              />
            </>
          )}

          {step === 2 && (
            <VoiceCloningSettings
              locale={language}
              onProceed={next}
              onSetupLater={next}
              showHeader={false}
            />
          )}

          {step === 3 && (
            <OnboardingReadyScreen
              name={name}
              onChat={() => { LocalStorage.setSidebar("chats"); navigate("/"); }}
              onCall={() => { LocalStorage.setSidebar("calls"); navigate("/"); }}
            />
          )}
        </Card>
      </Content>
    </Container>
  );
};

export default CreateNewUser;
